import { useTranslate } from '@tolgee/react';
import { Button, Drawer, DrawerProps } from 'antd';
import { Formik } from 'formik';
import {
  SaveKnowledge,
  SupplementKnowledgeAnswer,
  SupplementKnowledgeDetailResponse,
} from 'services/api/supplementKnowledge';
import { ProductInfo } from '../Detail/ProductInfo';
import { BuyerQuestion } from '../Detail/BuyerQuestion';
import { Section } from '../Detail/Section';
import { Answer } from '../Detail/Answer';
import { PureAnswerEditor } from '../Detail/AnswerEditor';
import FormField from 'components/FormField';
import styled from '@emotion/styled';
import AssisKnowledgeItem from './AssisKnowledgeItem';
import { LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { queries } from 'services/queries';
import { useConversationInfo } from 'hooks/business/useConversationInfo';

function AssisKnowledgeDrawer({
  visible,
  knowledge,
  sidebarDataSource,
  onKnowledgeChange,
  onClose,
  ...otherProps
}: DrawerProps & {
  knowledge: Pick<SaveKnowledge, 'value' | 'bindItem'>;
  sidebarDataSource: SupplementKnowledgeDetailResponse;
  onKnowledgeChange: (knowledge: Pick<SaveKnowledge, 'value' | 'bindItem'>) => void;
}) {
  const { id, itemIdentifier, conversationId, supplementalKnowledgeValue } = sidebarDataSource;

  const { answer, originAnswers } = supplementalKnowledgeValue;

  const submitAnswers = originAnswers?.length > 0 ? originAnswers : answer ? [answer] : [];

  const { t } = useTranslate();

  const { loading, conversation } = useConversationInfo({
    conversationId: `${conversationId}`,
    enabled: !!(visible && conversationId),
    readonly: true,
  });

  const { data } = useQuery({
    enabled: visible && !!id,
    ...queries.qaPair.knowledgeDetail(id),
  });

  return (
    <Formik
      key={`${visible}`}
      enableReinitialize
      initialValues={knowledge}
      onSubmit={(values) => {
        onKnowledgeChange(values);
        onClose?.({} as any);
      }}
    >
      {({ values, setValues, dirty, isValid, handleSubmit }) => {
        return (
          <Drawer
            visible={visible}
            title={t('修改引用知识')}
            width={1200}
            onClose={onClose}
            footer={
              <div className="flex justify-end">
                <Button className="mr-4" onClick={onClose as any}>
                  {t('退出')}
                </Button>
                <Button type="primary" disabled={!isValid || !dirty} onClick={() => handleSubmit()}>
                  {t('确认修改内容')}
                </Button>
              </div>
            }
            {...otherProps}
          >
            {conversation ? (
              <Container>
                <ProductInfo
                  knowledgeId={id}
                  submitItemIdentifier={itemIdentifier}
                  skuIds={supplementalKnowledgeValue.skuIds}
                  foldEnable={false}
                  dataSource={data || {}}
                  extra={
                    <AssisKnowledgeItem
                      knowledgeId={id}
                      storeId={Number(conversation.storeId)}
                      itemIdentifier={itemIdentifier}
                      bindItem={values.bindItem}
                      onBindItemChange={(bindItem) => setValues({ ...values, bindItem })}
                    />
                  }
                />
                <BuyerQuestion
                  citeMode
                  conversation={conversation}
                  supplementalKnowledgeValue={supplementalKnowledgeValue}
                />
                <Section title={t('客服提交答案')}>
                  <Answer
                    locate={false}
                    userName={data?.submittedUserName}
                    answers={submitAnswers}
                  />
                </Section>
                <Section title={t('修改答案')}>
                  <FormField<SupplementKnowledgeAnswer[]>
                    name="value"
                    validate={(value) => {
                      const fileValues = value.filter(({ msgType }) => msgType !== 'TEXT');
                      const textValue = value.find(({ msgType }) => msgType === 'TEXT');
                      if (!fileValues.length && !textValue?.message?.trim()) {
                        return t('department.请输入x', { x: t('答案/值') });
                      }
                    }}
                  >
                    {({ field: { value, onChange, onBlur } }) => {
                      return (
                        <PureAnswerEditor
                          citeMode
                          region={conversation.region}
                          channel={conversation.channel}
                          storeId={conversation.storeId}
                          answers={value}
                          onAnswersChange={(answers) => {
                            onBlur();
                            onChange(answers);
                          }}
                        />
                      );
                    }}
                  </FormField>
                </Section>
              </Container>
            ) : loading ? (
              <LoadingOutlined />
            ) : null}
          </Drawer>
        );
      }}
    </Formik>
  );
}

const Container = styled.div`
  margin: -24px;
  > * {
    margin-bottom: 24px;
  }
`;

export default AssisKnowledgeDrawer;
